import React, { useState, useEffect } from 'react';
import SharedCard   from './SharedCard';
import SharedInput  from './SharedInput';
import SharedButton from './SharedButton';
import { downloadReceiptAsImage } from '../utils/receiptToImage'; // FIX: helper de imagen

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

// ── Comprobante de registro ───────────────────────────────────────────────────
const buildReceiptHTML = ({ folio, fecha, hora, student, disciplineName, academyName }) => `<!doctype html>
<html lang="es"><head><meta charset="utf-8"/>
<title>Comprobante de Registro</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',system-ui,sans-serif;background:#f3f4f6;display:flex;justify-content:center;padding:40px 16px}
  .card{background:white;width:100%;max-width:520px;border-radius:20px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.12)}
  .header{background:linear-gradient(135deg,#961914,#c0392b);padding:32px;color:white}
  .header-top{display:flex;justify-content:space-between;align-items:flex-start}
  .brand h1{font-size:20px;font-weight:700}.brand p{font-size:12px;opacity:.75;margin-top:3px}
  .folio{background:rgba(255,255,255,.15);border-radius:10px;padding:8px 14px;text-align:right}
  .folio .num{font-size:16px;font-weight:700}.folio .lbl{font-size:11px;opacity:.7}
  .student{margin-top:20px;padding-top:20px;border-top:1px solid rgba(255,255,255,.2)}
  .student .lbl{font-size:11px;opacity:.7;text-transform:uppercase;letter-spacing:.5px}
  .student .name{font-size:20px;font-weight:700;margin-top:4px}
  .body{padding:24px 32px}
  .seal{display:flex;align-items:center;gap:8px;margin-bottom:16px}
  .seal-dot{width:8px;height:8px;border-radius:50%;background:#22c55e}
  .seal-text{font-size:12px;font-weight:600;color:#22c55e}
  .row{display:flex;justify-content:space-between;align-items:flex-start;padding:10px 0;border-bottom:1px solid #f9fafb}
  .row:last-child{border-bottom:none}
  .row .lbl{font-size:12px;color:#6b7280;min-width:160px}
  .row .val{font-size:13px;font-weight:600;color:#111827;text-align:right}
  .enrollments-table{width:100%;border-collapse:collapse;margin-top:4px}
  .enrollments-table th{font-size:11px;color:#6b7280;text-align:left;padding:4px 8px;background:#f9fafb}
  .enrollments-table td{font-size:12px;padding:4px 8px;border-bottom:1px solid #f3f4f6}
  .footer{padding:18px 32px;background:#fafafa;border-top:1px solid #f3f4f6}
  .footer p{font-size:11px;color:#9ca3af;line-height:1.7}
</style></head><body>
<div class="card">
  <div class="header">
    <div class="header-top">
      <div class="brand"><h1>${academyName || 'Academia de Danza'}</h1><p>Comprobante de registro</p></div>
      <div class="folio"><div class="lbl">Folio</div><div class="num">${folio}</div></div>
    </div>
    <div class="student"><div class="lbl">Estudiante registrada</div><div class="name">${student.name}</div></div>
  </div>
  <div class="body">
    <div class="seal"><div class="seal-dot"></div><span class="seal-text">REGISTRO CONFIRMADO</span></div>
    <div class="row"><span class="lbl">Padre / Madre / Tutor</span><span class="val">${student.parent_name || '—'}</span></div>
    <div class="row"><span class="lbl">Teléfono</span><span class="val">${student.phone || '—'}</span></div>
    <div class="row"><span class="lbl">Fecha de nacimiento</span><span class="val">${student.birth_date || '—'}</span></div>
    <div class="row"><span class="lbl">Disciplina principal</span><span class="val">${disciplineName || '—'}</span></div>
    <div class="row">
      <span class="lbl">Inscripciones</span>
      <span class="val">
        <table class="enrollments-table">
          <tr><th>Subdisciplina</th><th>Nivel</th></tr>
          ${(student.enrollments || []).map(e => `<tr><td>${e.subdiscipline}</td><td>${e.level}</td></tr>`).join('')}
        </table>
      </span>
    </div>
    <div class="row"><span class="lbl">Fecha de registro</span><span class="val">${fecha} · ${hora}</span></div>
  </div>
  <div class="footer"><p>Comprobante informativo del registro en el sistema. Para aclaraciones comuníquese con la administración.</p></div>
</div>
</body></html>`;

// FIX: reemplaza openReceipt — ahora descarga PNG directamente
const openReceipt = (html, folio, wa = null) => {
  downloadReceiptAsImage(html, folio || 'comprobante-registro', wa);
};

// ── Formulario ────────────────────────────────────────────────────────────────
const StudentForm = ({ currentUser, disciplines, editingStudent, onSaved, onCancel }) => {
  const isEditing = !!editingStudent;

  const [name,         setName]         = useState('');
  const [parentName,   setParentName]   = useState('');
  const [phone,        setPhone]        = useState('');
  const [birthDate,    setBirthDate]    = useState('');
  const [disciplineId, setDisciplineId] = useState('');
  const [status,       setStatus]       = useState('Activo');
  const [enrollments,  setEnrollments]  = useState([{ subdiscipline: '', level: '' }]);
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  useEffect(() => {
    if (editingStudent) {
      setName(editingStudent.name || '');
      setParentName(editingStudent.parent_name || '');
      setPhone(editingStudent.phone || '');
      setBirthDate(editingStudent.birth_date || '');
      setDisciplineId(String(editingStudent.main_discipline_id || ''));
      setStatus(editingStudent.status || 'Activo');
      setEnrollments(
        editingStudent.enrollments?.length > 0
          ? editingStudent.enrollments
          : [{ subdiscipline: '', level: '' }]
      );
    } else {
      resetForm();
    }
  }, [editingStudent]);

  const resetForm = () => {
    setName(''); setParentName(''); setPhone(''); setBirthDate('');
    setDisciplineId(''); setStatus('Activo');
    setEnrollments([{ subdiscipline: '', level: '' }]);
  };

  const selectedDisc    = disciplines.find(d => String(d.id) === String(disciplineId));
  const availableSubdis = selectedDisc?.subdisciplines || [];

  const handleEnrollmentChange = (idx, field, value) =>
    setEnrollments(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));

  const addEnrollment = () =>
    setEnrollments(prev => [...prev, { subdiscipline: '', level: '' }]);

  const removeEnrollment = idx => {
    if (enrollments.length === 1) return;
    setEnrollments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!name)         { showMsg('error', 'El nombre es obligatorio.'); return; }
    if (!disciplineId) { showMsg('error', 'Selecciona una disciplina principal.'); return; }
    const validEnrollments = enrollments.filter(e => e.subdiscipline && e.level);
    if (validEnrollments.length === 0) {
      showMsg('error', 'Agrega al menos una subdisciplina con su nivel.');
      return;
      // FIX: descarga PNG + abre WhatsApp si hay teléfono
openReceipt(html, folio, phone ? { phone, studentName: name, type: 'registro' } : null);
    }

    setSaving(true);
    const payload = {
      name, parent_name: parentName, phone, birth_date: birthDate,
      main_discipline_id: Number(disciplineId),
      status,
      enrollments: validEnrollments,
      ...(isEditing ? { id: editingStudent.id } : {}),
    };

    try {
      const endpoint = isEditing ? '/api/update_student.php' : '/api/create_student.php';
      const res  = await fetch(endpoint, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) { showMsg('error', data.message || 'Error al guardar.'); return; }

      showMsg('success', isEditing ? 'Estudiante actualizada.' : 'Estudiante registrada.');

      if (!isEditing) {
        const now   = new Date();
        const fecha = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
        const hora  = now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        const folio = data.student_id ? `STU-${String(data.student_id).padStart(6,'0')}` : `STU-${Date.now()}`;
        const html  = buildReceiptHTML({
          folio, fecha, hora,
          student: { ...payload, enrollments: validEnrollments },
          disciplineName: selectedDisc?.name || '',
          academyName: currentUser?.academy_name || '',
        });
        // FIX: descarga PNG directamente
        openReceipt(html, folio);
      }

      resetForm();
      onSaved();
    } catch {
      showMsg('error', 'Error de conexión.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-8">
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-lg font-bold text-gray-900">
          {isEditing ? `✏️ Editando: ${editingStudent.name}` : '➕ Registrar estudiante'}
        </h3>
        {isEditing && (
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        )}
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <SharedInput label="Nombre*" value={name} onChange={e => setName(e.target.value)} placeholder="Ej: María López" />
          <SharedInput label="Padre / Madre / Tutor" value={parentName} onChange={e => setParentName(e.target.value)} placeholder="Ej: Juan López" />
          <SharedInput label="Teléfono" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 5512345678" />
          <SharedInput label="Fecha de nacimiento" type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Disciplina principal*</label>
            <select
              value={disciplineId}
              onChange={e => { setDisciplineId(e.target.value); setEnrollments([{ subdiscipline: '', level: '' }]); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
            >
              <option value="">Selecciona disciplina</option>
              {disciplines.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Estado</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30"
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-bold text-gray-700">
              Inscripciones <span className="text-gray-400 font-normal">(subdisciplina + nivel)</span>
            </label>
            <button type="button" onClick={addEnrollment}
              className="text-xs font-semibold text-[#961914] hover:underline">
              + Agregar otra
            </button>
          </div>

          <div className="space-y-3">
            {enrollments.map((enroll, idx) => (
              <div key={idx} className="flex gap-3 items-start bg-gray-50 rounded-xl p-3 border border-gray-200">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Subdisciplina</label>
                  {availableSubdis.length > 0 ? (
                    <select value={enroll.subdiscipline}
                      onChange={e => handleEnrollmentChange(idx, 'subdiscipline', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30">
                      <option value="">Selecciona</option>
                      {availableSubdis.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={enroll.subdiscipline}
                      onChange={e => handleEnrollmentChange(idx, 'subdiscipline', e.target.value)}
                      placeholder="Ej: Ballet Clásico"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Nivel</label>
                  <input type="text" value={enroll.level}
                    onChange={e => handleEnrollmentChange(idx, 'level', e.target.value)}
                    placeholder="Ej: Principiantes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
                </div>
                {enrollments.length > 1 && (
                  <button type="button" onClick={() => removeEnrollment(idx)}
                    className="mt-6 text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <SharedButton type="submit" disabled={saving}>
            {saving ? 'Guardando…' : isEditing ? 'Guardar cambios' : 'Registrar estudiante'}
          </SharedButton>
          {isEditing && (
            <SharedButton onClick={onCancel} className="bg-gray-500 hover:bg-gray-600">Cancelar</SharedButton>
          )}
        </div>
      </form>
    </div>
  );
};

// ── Página principal ──────────────────────────────────────────────────────────
const StudentsPage = ({ currentUser }) => {
  const [students,       setStudents]       = useState([]);
  const [disciplines,    setDisciplines]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [editingStudent, setEditingStudent] = useState(null);
  const [filterStatus,   setFilterStatus]   = useState('Activo');
  const [filterDisc,     setFilterDisc]     = useState('');
  const [filterLevel,    setFilterLevel]    = useState('');
  const [search,         setSearch]         = useState('');
  const [msg,            setMsg]            = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const loadStudents = async () => {
    try {
      const res  = await fetch('/api/list_students.php', { credentials: 'include' });
      const data = await res.json();
      setStudents(Array.isArray(data) ? data.sort((a,b) => new Date(b.enrolled_at) - new Date(a.enrolled_at)) : []);
    } catch { setStudents([]); }
  };

  const loadDisciplines = async () => {
    try {
      const res  = await fetch('/api/list_disciplines.php', { credentials: 'include' });
      const data = await res.json();
      setDisciplines(Array.isArray(data) ? data : []);
    } catch { setDisciplines([]); }
  };

  useEffect(() => {
    Promise.all([loadStudents(), loadDisciplines()]).finally(() => setLoading(false));
  }, []);

  const handleSaved = () => { setEditingStudent(null); loadStudents(); };

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar esta estudiante?')) return;
    try {
      const res  = await fetch('/api/delete_student.php', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (data.success) { showMsg('success', 'Estudiante eliminada.'); loadStudents(); }
      else showMsg('error', data.message || 'Error al eliminar.');
    } catch { showMsg('error', 'Error de conexión.'); }
  };

  const allLevels = [...new Set(
    students.flatMap(s => (s.enrollments || []).map(e => e.level)).filter(Boolean)
  )].sort();

  const allDiscs = [...new Set(
    students.flatMap(s => (s.enrollments || []).map(e => e.subdiscipline)).filter(Boolean)
  )].sort();

  const getDisciplineName = id => disciplines.find(d => String(d.id) === String(id))?.name || '—';

  const handlePrintReceipt = student => {
    const now   = new Date(student.enrolled_at || Date.now());
    const fecha = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    const hora  = now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const folio = `STU-${String(student.id).padStart(6, '0')}`;
    const html  = buildReceiptHTML({
      folio, fecha, hora,
      student,
      disciplineName: getDisciplineName(student.main_discipline_id),
      academyName:    currentUser?.academy_name || '',
    });
    // FIX: descarga PNG + abre WhatsApp si hay teléfono
openReceipt(html, folio, student.phone ? { phone: student.phone, studentName: student.name, type: 'registro' } : null);
  };

  const filtered = students.filter(s => {
    const matchStatus = !filterStatus || (s.status || 'Activo') === filterStatus;
    const matchSearch = !search
      || s.name?.toLowerCase().includes(search.toLowerCase())
      || s.parent_name?.toLowerCase().includes(search.toLowerCase());
    const matchDisc  = !filterDisc  || (s.enrollments || []).some(e => e.subdiscipline === filterDisc);
    const matchLevel = !filterLevel || (s.enrollments || []).some(e => e.level === filterLevel);
    return matchStatus && matchSearch && matchDisc && matchLevel;
  });

  if (loading) return (
    <SharedCard title="Estudiantes">
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-[#961914] border-t-transparent rounded-full animate-spin" />
      </div>
    </SharedCard>
  );

  return (
    <SharedCard title="Estudiantes">
      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      <StudentForm
        currentUser={currentUser}
        disciplines={disciplines}
        editingStudent={editingStudent}
        onSaved={handleSaved}
        onCancel={() => setEditingStudent(null)}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Buscar</label>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Nombre o tutor…"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Subdisciplina</label>
          <select value={filterDisc} onChange={e => setFilterDisc(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30">
            <option value="">Todas</option>
            {allDiscs.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Nivel</label>
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30">
            <option value="">Todos</option>
            {allLevels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Estado</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30">
            <option value="Activo">Activos</option>
            <option value="Inactivo">Inactivos</option>
            <option value="">Todos</option>
          </select>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-3">Mostrando {filtered.length} de {students.length} estudiantes</p>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">🩰</div>
          <p className="text-sm">No hay estudiantes con estos filtros.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-[#961914] text-white text-xs uppercase tracking-wider">
                <th className="py-3 px-4 text-left">Estudiante</th>
                <th className="py-3 px-4 text-left">Tutor</th>
                <th className="py-3 px-4 text-left">Teléfono</th>
                <th className="py-3 px-4 text-left">Nacimiento</th>
                <th className="py-3 px-4 text-left">Inscripciones</th>
                <th className="py-3 px-4 text-left">Inscripción</th>
                <th className="py-3 px-4 text-center">Estado</th>
                <th className="py-3 px-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(student => (
                <tr key={student.id}
                  className={`hover:bg-gray-50 transition-colors ${editingStudent?.id === student.id ? 'bg-[#961914]/5' : ''}`}>
                  <td className="py-3 px-4">
                    <p className="font-semibold text-sm text-gray-900 whitespace-nowrap">{student.name}</p>
                    <p className="text-xs text-gray-400">{getDisciplineName(student.main_discipline_id)}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{student.parent_name || '—'}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{student.phone || '—'}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{student.birth_date || '—'}</td>
                  <td className="py-3 px-4">
                    {(student.enrollments || []).length === 0 ? (
                      <span className="text-gray-300 text-xs">—</span>
                    ) : (
                      <div className="space-y-1">
                        {student.enrollments.map((e, i) => (
                          <div key={i} className="flex gap-1 items-center">
                            <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{e.subdiscipline}</span>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">{e.level}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-400 whitespace-nowrap">
                    {student.enrolled_at ? new Date(student.enrolled_at).toLocaleDateString('es-MX') : '—'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                      (student.status||'').toLowerCase() === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${(student.status||'').toLowerCase() === 'activo' ? 'bg-green-500' : 'bg-red-400'}`} />
                      {student.status || 'Activo'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center gap-1 flex-wrap">
                      <button onClick={() => { setEditingStudent(student); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                        Editar
                      </button>
                      <button onClick={() => handlePrintReceipt(student)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors">
                        📸 Comprobante
                      </button>
                      <button onClick={() => handleDelete(student.id)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors">
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan="8" className="px-4 py-2 text-xs text-gray-400 text-right">
                  {filtered.length} estudiante{filtered.length !== 1 ? 's' : ''}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </SharedCard>
  );
};

export default StudentsPage;